import { Component, inject, signal } from '@angular/core'
import {
  NonNullableFormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms'
import { of } from 'rxjs'
import { tap, catchError } from 'rxjs/operators'

import {
  BaseDialogComponent,
  DialogRef,
  ErrorComponent,
  ReusableDialogComponent,
  InputComponent,
  TypographyComponent,
  ButtonComponent,
} from '@hyperkit'

import { AuthService } from '../../../../auth/auth.service'

@Component({
  selector: 'forgot-password-dialog',
  standalone: true,
  imports: [
    ReusableDialogComponent,
    ErrorComponent,
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    TypographyComponent,
    ButtonComponent,
  ],
  template: `
    <hyperkit-dialog
      class="hyperkit-component"
      [headerText]="options.headerText"
    >
      @if (!sentEmail()) {
        <hyperkit-typography
          variant="emphasized"
          size="sm"
          align="left"
          color="body"
        >
          Enter your email below to receive a link to reset your password.
        </hyperkit-typography>
        <form [formGroup]="forgotPasswordForm" (ngSubmit)="onSubmit()">
          <hyperkit-input
            class="hyperkit-component"
            type="email"
            id="email"
            label="Email"
            placeholder="Enter your email"
            formControlName="email"
          >
          </hyperkit-input>
          <hyperkit-button
            class="send-button"
            color="tertiary"
            text="Send"
            [disabled]="forgotPasswordForm.invalid"
          >
          </hyperkit-button>
        </form>
        @if (error()) {
          <hyperkit-error [error]="error()"></hyperkit-error>
        }
      }

      @if (sentEmail()) {
        <div class="dialog-inner-content">
          <hyperkit-typography
            variant="emphasized"
            size="sm"
            align="left"
            color="body"
          >
            Check your email for your password reset link.
          </hyperkit-typography>
        </div>
        <hyperkit-button
          class="send-button"
          color="tertiary"
          text="Return to Sign In"
          (click)="return()"
        >
        </hyperkit-button>
      }
    </hyperkit-dialog>
  `,
  styles: [
    `
      .send-button {
        float: right;
      }
      hyperkit-button {
        padding-top: 15px;
        padding-bottom: 15px;
      }
    `,
  ],
})
export class ForgotPasswordDialogComponent extends BaseDialogComponent<boolean> {
  private fb = inject(NonNullableFormBuilder)
  private authService = inject(AuthService)

  public error = signal<string>('')
  public sentEmail = signal<boolean>(false)

  public forgotPasswordForm = this.fb.group({
    email: [
      '',
      [
        Validators.required,
        Validators.pattern(
          '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,15}$',
        ),
      ],
    ],
  })

  protected initializeOptions(): void {
    this.options = { headerText: 'Forgot Password?' }
  }

  return(): void {
    this.dialogRef.close()
  }

  public onSubmit(): void {
    const email = this.forgotPasswordForm.get('email')?.value
    if (!email) return

    this.authService
      .forgotPassword(email)
      .pipe(
        tap(() => {
          this.options = { headerText: 'Success' }
          this.error.set('')
          this.sentEmail.set(true)
        }),
        catchError((err) => {
          console.error(err)
          this.error.set('There was an issue resetting the password.')
          return of(null)
        }),
      )
      .subscribe()
  }
}
